import { Button, If, Row, Rule } from "@hex-insights/core";
import {
	AddSubFormButtonRenderProps,
	Form,
	NumberField,
	SubFormField,
	SubFormRenderProps,
	TextAreaInput,
	TextField,
	TextFieldInputProps,
} from "@hex-insights/forms";
import {
	CourseAssessmentFormValues,
	CourseAssessmentTimingFormValues,
	MediaAttachmentFormValues,
} from "../../FormUtilities";
import { CourseAssessmentCategorySelectField } from "../CourseAssessmentCategory";
import { CourseAssessmentTimingField } from "../CourseAssessmentTiming";
import { MediaAttachmentCustomForm } from "../MediaAttachment";
import { BaseFieldProps } from "../shared";
import { UserSelectField } from "../User";
import { CourseAssessmentCategoryRadio, CourseAssessmentCategoryRadioProps } from "./CategoryRadio";

/**
 * Generic props for fields of the CourseAssessment model.
 */
type FieldProps<K extends keyof CourseAssessmentFormValues.Base = keyof CourseAssessmentFormValues.Base> =
	BaseFieldProps<Pick<CourseAssessmentFormValues.Base, K>>;

/**
 * Generic props for fields of the CourseAssessment model that only appear in the create form.
 */
type CreateFieldProps<K extends keyof CourseAssessmentFormValues.Create = keyof CourseAssessmentFormValues.Create> =
	BaseFieldProps<Pick<CourseAssessmentFormValues.Create, K>>;

/**
 * Generic props for fields of the CourseAssessment model that only appear in the detail form.
 */
type DetailFieldProps<K extends keyof CourseAssessmentFormValues.Detail = keyof CourseAssessmentFormValues.Detail> =
	BaseFieldProps<Pick<CourseAssessmentFormValues.Detail, K>>;

/**
 * Field component for the `name` field of the CourseAssessment model.
 */
export function Name({ formState, label = "Assessment Name", ...props }: FieldProps<"name">) {
	return <TextField formState={formState} name="name" label={label} {...props} />;
}

/**
 * Field component for the `description` field of the CourseAssessment model.
 */
export function Description({ formState, label = "Assessment Description", ...props }: FieldProps<"description">) {
	return (
		<TextField
			formState={formState}
			name="description"
			label={label}
			multiLine
			optional
			Input={DescriptionInput}
			{...props}
		/>
	);
}

function DescriptionInput(props: TextFieldInputProps) {
	return <TextAreaInput {...props} minRows={4} />;
}

/**
 * Field component for the `totalPoints` field of the CourseAssessment model.
 */
export function TotalPoints({ formState, ...props }: FieldProps<"totalPoints">) {
	return <NumberField formState={formState} name="totalPoints" validationUnit={1} min={0} {...props} />;
}

export namespace Author {
	export type SelectProps = DetailFieldProps<"authorID"> &
		Omit<UserSelectField.SelectProps<CourseAssessmentFormValues.Detail>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `author` edge of the CourseAssessment model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <UserSelectField.Select formState={formState} name="authorID" optional {...props} />;
	}
}

export namespace CourseAssessmentCategory {
	export type SelectProps = FieldProps<"courseAssessmentCategoryID"> &
		Omit<
			CourseAssessmentCategorySelectField.SelectProps<CourseAssessmentFormValues.Base>,
			"formState" | "name" | "optional"
		>;

	/**
	 * Field component for the `courseAssessmentCategory` edge of the CourseAssessment model.
	 */
	export function Select({ formState, label = "Type", ...props }: SelectProps) {
		return (
			<CourseAssessmentCategorySelectField.Select
				formState={formState}
				name="courseAssessmentCategoryID"
				label={label}
				{...props}
			/>
		);
	}

	export type RadioProps = FieldProps<"courseAssessmentCategoryID"> &
		Omit<CourseAssessmentCategoryRadioProps<CourseAssessmentFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `courseAssessmentCategory` edge of the CourseAssessment model (as radio).
	 */
	export function Radio({ formState, label = "Type", ...props }: RadioProps) {
		return (
			<CourseAssessmentCategoryRadio formState={formState} name="courseAssessmentCategoryID" label={label} {...props} />
		);
	}
}

export namespace CourseAssessmentTimings {
	/**
	 * SubForm field component for the `courseAssessmentTimings` edge of the CourseAssessment model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"courseAssessmentTimings">) {
		return (
			<SubFormField
				formState={formState}
				name="courseAssessmentTimings"
				minItems={1}
				blankItem={CourseAssessmentTimingFormValues.blankItemInCourseAssessment}
				dividerElement={<Rule thickness="1px" style={{ margin: "0.5rem 0" }} />}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		canRemoveItem,
		onRemoveClick,
	}: SubFormRenderProps<CourseAssessmentTimingFormValues.CreateInCourseAssessment>) {
		return (
			<Form.Column>
				<Row justify="spaced-start" align="flex-end" overflow="wrap" withVerticalSpacing>
					<CourseAssessmentTimingField.CourseSection.Select formState={formState} style={{ flexGrow: 1 }} />
					<If condition={canRemoveItem}>
						<Button
							variant="tertiary"
							size="small"
							onClick={onRemoveClick}
							disabled={formState.disabled}
							style={{ marginBottom: "0.25rem" }}
						>
							Remove Course
						</Button>
					</If>
				</Row>

				<Row justify="spaced-start" overflow="wrap" withVerticalSpacing>
					<CourseAssessmentTimingField.AssignOn formState={formState} noOptional />
					<CourseAssessmentTimingField.DueOn formState={formState} noOptional />
				</Row>
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
		numItems,
	}: AddSubFormButtonRenderProps<
		Pick<CourseAssessmentFormValues.Create, "courseAssessmentTimings">,
		CourseAssessmentTimingFormValues.CreateInCourseAssessment
	>) {
		return (
			<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
				{numItems === 0 ? "Add to Course" : "Add to Another Course"}
			</Button>
		);
	};
}

export namespace MediaAttachments {
	/**
	 * SubForm field component for the `mediaAttachments` edge of the CourseAssessment model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"mediaAttachments">) {
		return (
			<MediaAttachmentCustomForm.SubForm
				formState={formState}
				name="mediaAttachments"
				blankItem={MediaAttachmentFormValues.blankItemInCourseAssessment}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</MediaAttachmentCustomForm.SubForm>
		);
	}

	SubForm.SubForm = function (props: SubFormRenderProps<MediaAttachmentFormValues.CreateInCourseAssessment>) {
		return (
			<MediaAttachmentCustomForm.SubForm.SubForm {...props}>
				<MediaAttachmentCustomForm.CreateFieldsInCourseAssessment formState={props.formState} />
			</MediaAttachmentCustomForm.SubForm.SubForm>
		);
	};

	SubForm.AddButton = function (
		props: AddSubFormButtonRenderProps<
			Pick<CourseAssessmentFormValues.Create, "mediaAttachments">,
			MediaAttachmentFormValues.CreateInCourseAssessment
		>,
	) {
		return <MediaAttachmentCustomForm.SubForm.AddButton {...props} />;
	};
}
